import {
  BrowserCacheLocation,
  BrowserUtils,
  Configuration,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication
} from '@azure/msal-browser';
import {
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer
} from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalService
} from '@azure/msal-angular';
import {
  provideRouter,
  Route,
  withDisabledInitialNavigation,
  withEnabledBlockingInitialNavigation
} from '@angular/router';
import { HttpFeature, HttpFeatureKind, provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from '../interceptors/auth.interceptor';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { catchError, of, tap } from 'rxjs';
import { RightsStorage } from '../services/rights.storage';

const msalConfig: Configuration = {
  auth: {
    clientId: AUTH_CONFIG.CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
    authority: AUTH_CONFIG.AUTHORITY, // Replace the placeholder with your tenant subdomain
    redirectUri: AUTH_CONFIG.REDIRECT_URI, // Points to window.location.origin by default. You must register this URI on Microsoft Entra admin center/App Registration.
    postLogoutRedirectUri: '/', // Points to window.location.origin by default.
    knownAuthorities: AUTH_CONFIG.KNOWN_AUTHORITIES
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
  },
  system: {
    loggerOptions: {
      loggerCallback(logLevel: LogLevel, message: string) {
        // console.log(message);
      },
      logLevel: LogLevel.Verbose,
      piiLoggingEnabled: false,
    },
  },
};
const loginRequest = {
  scopes: [],
};

const interceptorRequest = {
  redirectUri: msalConfig.auth.redirectUri
};


function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
function MsalGuardConfigurationFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest
  };
}

export function provideAuth(): EnvironmentProviders {

  return makeEnvironmentProviders([
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MsalGuardConfigurationFactory
    },
    MsalService,
    MsalBroadcastService,
    MsalGuard
  ]);
}

export function provideAuthHttp(...features: HttpFeature<HttpFeatureKind>[]): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideHttpClient(...features, withInterceptors([authInterceptor]))
  ]);
}

export function provideAuthRouter(routes: Route[] = []): EnvironmentProviders {

  return makeEnvironmentProviders([
    provideRouter(routes, !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup()
      ? withEnabledBlockingInitialNavigation()
      : withDisabledInitialNavigation()),
  ]);
}

export function rightsManagement() {
  return makeEnvironmentProviders([
    importProvidersFrom(NgxPermissionsModule.forRoot()),
    provideAppInitializer(
      () => {
        const ngxPermissionService = inject(NgxPermissionsService);
        const rightStorage = inject(RightsStorage);
        return rightStorage.getData()
          .pipe(
            catchError(_ => of([])),
            tap(roles => ngxPermissionService.loadPermissions(roles))
          );
      }
    ),
  ])
}
